import * as React from 'react'
import { useCallback, useState, useEffect } from 'react'
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
} from '@material-ui/core'
import { t } from 'ttag'
import {
  convertCmToFtIn,
  convertFtInToCm,
  DATE_FORMAT,
  isDateOfBirthErrorVisible,
  isDateOfBirthValid,
  isNumberFieldErrorVisible,
  precisionRoundMod,
} from '../../../common/helpers/formHelpers'
import dayjs from '../../../dayjsConfig'
import convert from 'convert-units'
import { openActivityIndexArticle } from '../../../common/helpers/routeHelpers'
import { Gender, Lang, ServiceProvider, Units, UserCompanyDto, UserProfileDto } from '../../../services/api.types'
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage'
import Typography from '@material-ui/core/Typography'
import YumInput from '../../../components/YumInput/YumInput'
import YumDateOfBirth from '../../../components/YumDateOfBirth/YumDateOfBirth'
import YumSelect from '../../../components/YumSelect/YumSelect'
import YumButtonGroup from '../../../components/YumButtonGroup/YumButtonGroup'
import YumSubmitButton from '../../../components/YumSubmitButton/YumSubmitButton'
import YumCancelButton from '../../../components/YumCancelButton/YumCancelButton'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'
import { currentLang } from '../../../i18nInit'
import {
  unitsCountLengthCentimeterShort,
  unitsCountLengthFootShort,
  unitsCountLengthInchShort,
  unitsCountMassKilogramShort,
  unitsCountMassPoundShort,
} from '../../../common/helpers/units'
import { ReactComponent as DateActiveIcon } from '../../../assets/v3/icons/date-active.svg'
import { ReactComponent as DateInactiveIcon } from '../../../assets/v3/icons/date-inactive.svg'
import { ReactComponent as WeightActiveIcon } from '../../../assets/v3/icons/weight-active.svg'
import { ReactComponent as WeightInactiveIcon } from '../../../assets/v3/icons/weight-inactive.svg'
import { ReactComponent as HeightActiveIcon } from '../../../assets/v3/icons/height-active.svg'
import { ReactComponent as HeightInactiveIcon } from '../../../assets/v3/icons/height-inactive.svg'
import { ReactComponent as CheckboxIcon } from '../../../assets/v3/icons/checkbox.svg'
import { ReactComponent as CheckboxSelectedIcon } from '../../../assets/v3/icons/checkbox-selected.svg'

// Validation constants
export const MIN_HEIGHT_CM = 100
export const MIN_WEIGHT_KG = 25

const timeZoneGuess = dayjs.tz.guess()
const isUsa = timeZoneGuess.startsWith('America/') || timeZoneGuess.startsWith('US/')

export interface ActivityFormProps {
  // Props from parent
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  lastUpdate: number | null

  // State setters (for parent coordination)
  setIsSubmitInProgress: (value: boolean) => void

  // Handlers
  updateActivityData: (payload: any) => void
  initialUnits: Units.METRIC | Units.IMPERIAL | null
}

export const ActivityForm: React.FC<ActivityFormProps> = ({
  userDto,
  userCompanies,
  lastUpdate,
  setIsSubmitInProgress,
  updateActivityData,
  initialUnits,
}) => {
  // Form state - moved from parent component
  const [dateOfBirth, setDateOfBirth] = useState<string>('')
  const [gender, setGender] = useState<Gender.MALE | Gender.FEMALE>(Gender.MALE)
  const [units, setUnits] = useState<Units.METRIC | Units.IMPERIAL>(
    initialUnits ?? (isUsa ? Units.IMPERIAL : Units.METRIC),
  )
  const [weight, setWeight] = useState<string | number>('')
  const [weightLbs, setWeightLbs] = useState<string | number>('')
  const [height, setHeight] = useState<string | number>('')
  const [heightFt, setHeightFt] = useState<string | number>('3')
  const [heightIn, setHeightIn] = useState<string | number>('')
  const [isActivityIndexFieldRequired, setIsActivityIndexFieldRequired] = useState<boolean>(true)

  // Internal state for form validation and submit progress
  const [activityDataFormValid, setActivityDataFormValid] = useState<boolean>(true)
  const [isSubmitInProgress, setIsSubmitInProgressLocal] = useState<boolean>(false)

  // Handle userDto serviceProvider prop change - populate form data from integration (activity fields only)
  useEffect(() => {
    const { serviceProvider: userServiceProvider } = userDto

    // @ts-ignore
    if (userServiceProvider !== '' && userServiceProvider !== ServiceProvider.APPLE_HEALTH) {
      const {
        dateOfBirth: userDateOfBirth,
        gender: userGender,
        weight: userWeight,
        height: userHeight,
        units: userUnits,
      } = userDto

      const dateOfBirthFormatted = userDateOfBirth ? dayjs(userDateOfBirth).format(DATE_FORMAT) : ''
      const genderValue = userGender ? userGender : Gender.MALE

      // Calculate height in feet/inches from userHeight (in cm)
      const userHeightStr = userHeight ? String(userHeight) : '0'
      const { heightFt: calculatedHeightFt, heightIn: calculatedHeightIn } = convertCmToFtIn(userHeightStr)
      const calculatedWeightLbs = precisionRoundMod(convert(userWeight).from('kg').to('lb'), 5)

      setDateOfBirth(dateOfBirthFormatted)
      setGender(genderValue)
      setWeight(userWeight)
      setHeight(userHeight)
      setHeightFt(calculatedHeightFt)
      setHeightIn(calculatedHeightIn)
      setWeightLbs(calculatedWeightLbs)

      if (userUnits) setUnits(userUnits)
    }
  }, [userDto.serviceProvider, userDto])

  // Clear local loading state when lastUpdate changes (success or error)
  // This ensures the loader stops spinning even when errors occur
  useEffect(() => {
    if (lastUpdate !== undefined && lastUpdate !== null) {
      setIsSubmitInProgressLocal(false)
    }
  }, [lastUpdate])

  // Handle input changes for weight, weightLbs, height - with unit conversions
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target

    if (['weight', 'weightLbs', 'height'].includes(name)) {
      // Update the corresponding state
      if (name === 'weight') setWeight(value)
      if (name === 'weightLbs') setWeightLbs(value)
      if (name === 'height') setHeight(value)
    }

    // Convert weight between kg and lbs
    if ('weight' === name) {
      let convertedWeightLbs = convert(parseFloat(value)).from('kg').to('lb')
      convertedWeightLbs = precisionRoundMod(convertedWeightLbs, 5)
      setWeightLbs(convertedWeightLbs)
    }

    if ('weightLbs' === name) {
      let convertedWeight = convert(parseFloat(value)).from('lb').to('kg')
      convertedWeight = precisionRoundMod(convertedWeight, 5)
      setWeight(convertedWeight)
    }

    if ('height' === name) {
      const { heightFt: calculatedHeightFt, heightIn: calculatedHeightIn } = convertCmToFtIn(value)
      setHeightFt(calculatedHeightFt)
      setHeightIn(calculatedHeightIn)
    }
  }, [])

  // Handle height in feet change - convert to cm
  const handleHeightFtChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value: number = parseInt(event.target.value as string)
      const calculatedHeight = convertFtInToCm(value, parseInt(heightIn as string))
      setHeightFt(value)
      setHeight(calculatedHeight)
    },
    [heightIn],
  )

  // Handle height in inches change - convert to cm
  const handleHeightInChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value: number = parseInt(event.target.value as string)
      const calculatedHeight = convertFtInToCm(parseInt(heightFt as string), value)
      setHeightIn(value)
      setHeight(calculatedHeight)
    },
    [heightFt],
  )

  // Check if activity index view is required based on feature flag
  const requireActivityIndexView = hasFeatureFlag(userCompanies, 'requireActivityIndexView')

  // Validation function - checks if activity data form is valid
  const isActivityDataFormValid = useCallback(() => {
    let isWeightInvalid
    if (typeof weight === 'string') {
      isWeightInvalid = weight ? parseInt(weight, 10) < MIN_WEIGHT_KG || weight.length === 0 : true
    } else {
      isWeightInvalid = weight ? weight < MIN_WEIGHT_KG : true
    }
    let isHeightInvalid
    if (typeof height === 'string') {
      isHeightInvalid = height ? parseInt(height, 10) < MIN_HEIGHT_CM || height.length === 0 : true
    } else {
      isHeightInvalid = height ? height < MIN_HEIGHT_CM : true
    }

    return !(!isDateOfBirthValid(dateOfBirth) || isWeightInvalid || isHeightInvalid)
  }, [dateOfBirth, weight, height])

  // Submit handler - validates and submits activity data
  const handleSubmit = useCallback(() => {
    setIsActivityIndexFieldRequired(true)
    const formValid = isActivityDataFormValid()
    setActivityDataFormValid(formValid)

    if (formValid) {
      setIsSubmitInProgressLocal(true)
      setIsSubmitInProgress(true)
      updateActivityData({
        dateOfBirth: dayjs(dateOfBirth, DATE_FORMAT).locale('en').format('YYYY-MM-DD'),
        gender,
        height,
        weight,
        units,
      })
    }
  }, [
    dateOfBirth,
    gender,
    height,
    weight,
    units,
    isActivityDataFormValid,
    setIsActivityIndexFieldRequired,
    setIsSubmitInProgress,
    updateActivityData,
  ])

  // Skip handler - submits empty activity data
  const handleSkip = useCallback(() => {
    setIsSubmitInProgressLocal(true)
    setIsSubmitInProgress(true)
    updateActivityData({})
  }, [setIsSubmitInProgress, updateActivityData])

  // Internal handlers - these are only used in this component
  const handleDOBChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDateOfBirth(event.target.value)
  }, [])

  const handleGenderChange = useCallback((event: any, genderValue: any) => {
    setGender(genderValue)
  }, [])

  const activityIndexNotice =
    currentLang === Lang.EN || currentLang === Lang.ET
      ? t`We need this data for activity index calculation. If you only want to participate in other types of challenges, you can skip this.`
      : t`We need this data for activity index calculation. If you only want to participate in step or manual data entry challenges, you can skip this.`

  return (
    <>
      <Typography variant="subtitle2" style={{ marginBottom: '24px' }}>
        {t`YuMuuv Activity Index`}
      </Typography>

      {/* Info notice */}
      <div className="info">
        <span>{activityIndexNotice}</span>
        {` `}
        <button type="button" className="link-blue" onClick={openActivityIndexArticle}>
          {t`Learn more`}
        </button>
      </div>

      {/* Gender Selection */}
      <FormGroup row={true}>
        <FormControl component="fieldset" style={{ margin: '0 10px 12px 10px' }}>
          <FormLabel required={isActivityIndexFieldRequired} component="legend" style={{ fontSize: '0.75rem' }}>
            {t`Sex`}
          </FormLabel>
          <RadioGroup
            row={true}
            name="sex"
            aria-label={t`Sex`}
            defaultValue={gender ? gender : Gender.MALE}
            value={gender ? gender : Gender.MALE}
            onChange={handleGenderChange}
          >
            <FormControlLabel
              control={<Radio icon={<CheckboxIcon />} checkedIcon={<CheckboxSelectedIcon />} />}
              value={Gender.MALE.toString()}
              label={t`Male`}
            />
            <FormControlLabel
              control={<Radio icon={<CheckboxIcon />} checkedIcon={<CheckboxSelectedIcon />} />}
              value={Gender.FEMALE.toString()}
              label={t`Female`}
            />
          </RadioGroup>
        </FormControl>
      </FormGroup>

      {/* Date of Birth */}
      <div style={{ marginBottom: '24px' }}>
        <YumDateOfBirth
          label={t`Date of birth`}
          // helperText={
          //   isDateOfBirthErrorVisible(dateOfBirth, !isActivityIndexFieldRequired || activityDataFormValid)
          //     ? undefined
          //     : t`Format: DD.MM.YYYY`
          // }
          //startIcon={DateActiveIcon}
          value={dateOfBirth}
          onChange={handleDOBChange}
          error={isDateOfBirthErrorVisible(dateOfBirth, !isActivityIndexFieldRequired || activityDataFormValid)}
          required={isActivityIndexFieldRequired}
          style={{
            marginBottom: 0,
          }}
        />
        {isDateOfBirthErrorVisible(dateOfBirth, !isActivityIndexFieldRequired || activityDataFormValid) && (
          <ErrorMessage>{t`Please enter a valid date.`}</ErrorMessage>
        )}
      </div>

      {/* Metric Units Inputs */}
      {units === Units.METRIC && (
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <div style={{ marginBottom: 16 }}>
              <YumInput
                startIcon={WeightActiveIcon}
                startIconInactive={WeightInactiveIcon}
                variant="filled"
                label={t`Weight`}
                type="text"
                inputProps={{ inputMode: 'numeric', 'aria-required': 'true', min: MIN_WEIGHT_KG }}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: <InputAdornment position="end">{unitsCountMassKilogramShort}</InputAdornment>,
                }}
                name="weight"
                value={weight || ''}
                required={isActivityIndexFieldRequired}
                error={isNumberFieldErrorVisible(
                  weight as string,
                  !isActivityIndexFieldRequired || activityDataFormValid,
                  MIN_WEIGHT_KG,
                )}
                style={{
                  marginBottom: 0,
                }}
                onChange={handleInputChange}
              />
              {isNumberFieldErrorVisible(
                weight as string,
                !isActivityIndexFieldRequired || activityDataFormValid,
                MIN_WEIGHT_KG,
              ) && <ErrorMessage>{t`Weight must be at least ${MIN_WEIGHT_KG} kg.`}</ErrorMessage>}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div style={{ marginBottom: 16 }}>
              <YumInput
                startIcon={HeightActiveIcon}
                startIconInactive={HeightInactiveIcon}
                variant="filled"
                label={t`Height`}
                value={height || ''}
                required={isActivityIndexFieldRequired}
                error={isNumberFieldErrorVisible(
                  height as string,
                  !isActivityIndexFieldRequired || activityDataFormValid,
                  MIN_HEIGHT_CM,
                )}
                type="text"
                inputProps={{ inputMode: 'numeric', 'aria-required': 'true', min: MIN_HEIGHT_CM }}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: <InputAdornment position="end">{unitsCountLengthCentimeterShort}</InputAdornment>,
                }}
                name="height"
                onChange={handleInputChange}
                style={{
                  marginBottom: '0px',
                }}
              />
              {isNumberFieldErrorVisible(
                height as string,
                !isActivityIndexFieldRequired || activityDataFormValid,
                MIN_HEIGHT_CM,
              ) && <ErrorMessage>{t`Height must be at least ${MIN_HEIGHT_CM} cm.`}</ErrorMessage>}
            </div>
          </Grid>
        </Grid>
      )}

      {/* Imperial Units Inputs */}
      {units === Units.IMPERIAL && (
        <Grid container spacing={3} style={{ marginBottom: 0 }}>
          <Grid item xs={12} sm={5} style={{ paddingBottom: 0 }}>
            <YumInput
              startIcon={WeightActiveIcon}
              startIconInactive={WeightInactiveIcon}
              style={{ width: '100%', marginBottom: 0 }}
              variant="filled"
              label={t`Weight`}
              type="text"
              inputProps={{ inputMode: 'numeric', 'aria-required': 'true' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{unitsCountMassPoundShort}</InputAdornment>,
              }}
              name="weightLbs"
              value={weightLbs || ''}
              required={isActivityIndexFieldRequired}
              error={isNumberFieldErrorVisible(
                weight as string,
                !isActivityIndexFieldRequired || activityDataFormValid,
                MIN_WEIGHT_KG,
              )}
              onChange={handleInputChange}
            />

            {isNumberFieldErrorVisible(
              weight as string,
              !isActivityIndexFieldRequired || activityDataFormValid,
              MIN_WEIGHT_KG,
            ) && (
              <div style={{ marginBottom: '0' }}>
                <ErrorMessage>
                  {t`Weight must be at least ${Math.ceil(convert(MIN_WEIGHT_KG).from('kg').to('lb'))} lb.`}
                </ErrorMessage>
              </div>
            )}
          </Grid>
          <Grid item xs={12} sm={7}>
            <Grid container spacing={1} style={{ marginBottom: 0 }}>
              <Grid item xs={6}>
                <YumSelect
                  startIcon={HeightInactiveIcon}
                  label={t`Height`}
                  value={heightFt}
                  onChange={handleHeightFtChange}
                  error={isNumberFieldErrorVisible(
                    height as string,
                    !isActivityIndexFieldRequired || activityDataFormValid,
                    MIN_HEIGHT_CM,
                  )}
                >
                  {[...Array(7)].map((e, i) => {
                    i += 3
                    return (
                      <option key={i} value={i}>
                        {i} {unitsCountLengthFootShort}
                      </option>
                    )
                  })}
                </YumSelect>
              </Grid>
              <Grid item xs={6}>
                <YumSelect
                  label={t`Height`}
                  value={heightIn}
                  onChange={handleHeightInChange}
                  error={isNumberFieldErrorVisible(
                    height as string,
                    !isActivityIndexFieldRequired || activityDataFormValid,
                    MIN_HEIGHT_CM,
                  )}
                >
                  {[...Array(12)].map((e, i) => {
                    return (
                      <option key={i} value={i}>
                        {i} {unitsCountLengthInchShort}
                      </option>
                    )
                  })}
                </YumSelect>
              </Grid>
            </Grid>

            {isNumberFieldErrorVisible(
              height as string,
              !isActivityIndexFieldRequired || activityDataFormValid,
              MIN_HEIGHT_CM,
            ) && (
              <div style={{ marginTop: '-4px', marginBottom: '4px' }}>
                <ErrorMessage>
                  {t`Height must be at least ${Math.floor(MIN_HEIGHT_CM / 30.48)} ft ${Math.ceil(
                    (MIN_HEIGHT_CM % 30.48) / 2.54,
                  )} in.`}
                </ErrorMessage>
              </div>
            )}
          </Grid>
        </Grid>
      )}

      {/* Action Buttons */}
      <YumButtonGroup>
        {/* Only show skip button if activity index view is not required */}
        {!requireActivityIndexView && (
          <YumCancelButton text={t`Skip`} onClick={handleSkip} disabled={isSubmitInProgress} />
        )}
        <YumSubmitButton text={t`Save`} onClick={handleSubmit} disabled={isSubmitInProgress} />
      </YumButtonGroup>
    </>
  )
}
